/**
 * 매장 선택 (/my/shops/ · page-partner-shops.php).
 *
 * 통합 관리 계정 · 관리자가 [이 매장 관리]를 누르면 admin-ajax(Partner_Shops)로 보내 세션을 그 매장 주인으로 바꾸고,
 * 서버가 준 주소(/setup/)로 옮긴다. 알림은 옮긴 화면에서 띄운다(sessionStorage).
 *
 * 관리자는 전체 목록 대신 검색 폼이 있다 — 검색 결과를 서버가 그린 카드와 같은 마크업으로 그린다.
 */

import { busy } from './busyButton';
import { postForm, reloadWith, setNotice, showFlash } from './partnerForm';

/** 옮긴 화면에 띄울 알림을 맡겨 두는 자리 */
const FLASH_KEY = 'qrshop_shops_flash';

/** 검색 결과 카드 — page-partner-shops.php 의 카드와 같은 클래스 */
const renderCard = (shop, currentId) => {
  const li = document.createElement('li');
  const isCurrent = Number(shop.id) === currentId;
  const available = false !== shop.available;

  li.className = `card shops__card${isCurrent ? ' shops__card--current' : ''}${available ? '' : ' shops__card--off'}`;

  const head = document.createElement('div');
  head.className = 'shops__head';

  const title = document.createElement('h2');
  title.className = 'shops__title';
  title.textContent = shop.title || `#${shop.id}`;
  head.append(title);

  const badge = (text, kind) => {
    const span = document.createElement('span');
    span.className = `badge badge--${kind}`;
    span.textContent = text;
    head.append(span);
  };

  if (isCurrent) {
    badge('현재', 'brand');
  }

  if (shop.own) {
    badge('내 매장', 'info');
  }

  if (!available) {
    badge('사용 불가', 'warning');
  }

  li.append(head);

  const meta = (text) => {
    const p = document.createElement('p');
    p.className = 'shops__meta';
    p.textContent = text;
    li.append(p);
  };

  if (shop.user_login) {
    meta(`아이디 ${shop.user_login}`);
  }

  if (shop.address) {
    meta(shop.address);
  }

  if (!available) {
    const reason = document.createElement('p');
    reason.className = 'shops__reason';
    reason.textContent = shop.reason || '지금은 관리할 수 없는 매장입니다.';
    li.append(reason);

    return li;
  }

  const action = document.createElement('div');
  action.className = 'shops__action';

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'button button--primary button--block';
  button.dataset.shopsSelect = String(shop.id);
  button.textContent = isCurrent ? '관리 중' : '이 매장 관리';

  if (isCurrent) {
    button.disabled = true;
    button.setAttribute('data-current', '');
  }

  action.append(button);
  li.append(action);

  return li;
};

const InitPartnerShops = () => {
  const root = document.querySelector('[data-shops]');

  if (!root) {
    return;
  }

  showFlash(FLASH_KEY);

  const status = root.querySelector('[data-shops-status]');
  const currentId = Number(root.dataset.current || 0);

  // 보내는 동안 다른 버튼(검색 결과 포함)은 disabled 만, 도는 원은 실제로 누른 버튼에만.
  // 실패하면 현재 매장 버튼만 잠근 채 되돌린다
  const lockOthers = (button, disabled) => {
    root.querySelectorAll('[data-shops-select]').forEach((item) => {
      if (item !== button) {
        item.disabled = disabled || item.hasAttribute('data-current');
      }
    });
  };

  root.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-shops-select]');

    if (!button || button.disabled) {
      return;
    }

    lockOthers(button, true);
    const release = busy(button, '여는 중…');
    setNotice(status, '매장을 여는 중입니다...', 'warning');

    try {
      const data = await postForm(root, 'qrshop_shops_select', {
        shop_id: button.dataset.shopsSelect,
      });

      reloadWith(FLASH_KEY, data.message || '매장을 골랐습니다.', data.redirect || '');
    } catch (error) {
      setNotice(status, error.message);
      release();
      lockOthers(button, false);
    }
  });

  // ── 관리자 검색 ─────────────────────────────────────────────────────────
  const form = root.querySelector('[data-shops-search]');
  const results = root.querySelector('[data-shops-results]');
  const resultLabel = root.querySelector('[data-shops-result-label]');

  if (!form || !results) {
    return;
  }

  const input = form.elements.namedItem('q');
  const submit = form.querySelector('[data-shops-search-submit]');

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const q = input.value.trim();

    if ('' === q) {
      setNotice(status, '검색어를 입력해 주세요.');
      input.focus();
      return;
    }

    const release = busy(submit, '찾는 중…');
    setNotice(status, '');

    try {
      const data = await postForm(root, 'qrshop_shops_search', { q });
      const shops = Array.isArray(data.shops) ? data.shops : [];

      results.replaceChildren(...shops.map((shop) => renderCard(shop, currentId)));
      results.hidden = 0 === shops.length;

      if (resultLabel) {
        resultLabel.textContent =
          0 === shops.length
            ? `"${data.q || q}" 에 맞는 매장이 없습니다.`
            : `"${data.q || q}" 검색 결과 ${shops.length}개`;
        resultLabel.hidden = false;
      }
    } catch (error) {
      setNotice(status, error.message);
    } finally {
      release();
    }
  });
};

export default InitPartnerShops;
